import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { BRANDS } from "@/data/content";
import { Reveal, MaskedLines, Eyebrow } from "./Reveal";

const BrandCard = ({ b, flip }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--x", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--y", `${e.clientY - r.top}px`);
  };
  return (
    <Reveal>
      <article
        ref={ref}
        onMouseMove={onMove}
        data-testid={`brand-card-${b.id}`}
        style={{ "--accent": b.accent }}
        className="spotlight group relative grid items-center gap-10 overflow-hidden rounded-[2rem] border border-brand-indigo/10 bg-white p-6 sm:p-10 lg:grid-cols-12 lg:gap-16"
      >
        <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
          <div className="clip-frame relative aspect-[4/3] overflow-hidden rounded-[1.5rem] bg-brand-navy">
            <img src={b.image} alt={b.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent" />
            <div className="absolute bottom-5 left-5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/85">{b.domain}</div>
            <div className="absolute right-5 top-5 h-3 w-3 rounded-full" style={{ background: b.accent, boxShadow: `0 0 18px ${b.accent}` }} />
          </div>
        </div>
        <div className="relative lg:col-span-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: b.accent }}>{b.kicker}</div>
          <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-brand-ink sm:text-4xl">{b.name}</h3>
          <p className="mt-2 font-display text-base text-brand-indigo/80 md:text-lg">{b.tagline}</p>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">{b.description}</p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {b.services.map((s) => (
              <li key={s} className="rounded-full border border-brand-indigo/15 bg-brand-ice px-4 py-1.5 text-xs text-brand-ink/80 sm:text-sm">{s}</li>
            ))}
          </ul>
          <a
            data-testid={`brand-link-${b.id}`}
            href={b.url}
            target="_blank"
            rel="noreferrer"
            className="group/link mt-10 inline-flex items-center gap-3 font-display font-semibold text-brand-indigo"
          >
            <span className="link-underline">Visit {b.domain}</span>
            <ArrowUpRight className="h-5 w-5 transition-transform duration-500 group-hover/link:-translate-y-1 group-hover/link:translate-x-1" />
          </a>
        </div>
      </article>
    </Reveal>
  );
};

export const Brands = () => (
  <section id="brands" data-testid="brands-section" className="relative bg-brand-ice py-28 lg:py-40">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow n="01" label="Our brands" /></Reveal>
          <MaskedLines
            as="h2"
            inView
            delay={0.1}
            className="mt-6 font-display text-3xl font-bold tracking-tight text-brand-ink sm:text-4xl lg:text-5xl"
            lines={["Two specialist brands.", "One standard of care."]}
          />
        </div>
        <Reveal delay={0.2} className="lg:col-span-5">
          <p className="text-base leading-relaxed text-slate-600 md:text-lg">
            Each brand is a focused team with its own domain expertise, sharing Malansh's operating backbone — recruitment,
            quality framework and US-hours coverage.
          </p>
        </Reveal>
      </div>

      <div className="mt-20 flex flex-col gap-10">
        {BRANDS.map((b, i) => <BrandCard key={b.id} b={b} flip={i % 2 === 1} />)}
      </div>
    </div>
  </section>
);
